<template>
    <div class="motivation-marginality-chart">
        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Загрузка...</span>
            </div>
            <p class="mt-2">Загрузка данных маржинальности...</p>
        </div>
        <div v-else-if="rawChartData && rawChartData.length > 0" class="chart-wrapper">
            <CustomLineChart 
                :chartData="chartData" 
                :options="chartOptions"
            />
        </div>
        <div v-else class="text-center text-muted">
            <i class="fas fa-chart-line fa-3x mb-3"></i>
            <p>Нет данных для отображения графика маржинальности</p>
        </div>
    </div>
</template>

<script>
import CustomLineChart from './CustomLineChart.vue'

export default {
    name: 'MotivationMarginalityChart',
    components: {
        CustomLineChart
    },
    props: {
        rawChartData: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Маржинальность (₽)'
        }
    },
    computed: {
        chartData() {
            if (!this.rawChartData || this.rawChartData.length === 0) {
                return {
                    labels: [],
                    datasets: []
                }
            }

            const labels = this.rawChartData.map(item => item.month_name)
            const data = this.rawChartData.map(item => item.total_margin)

            return {
                labels: labels,
                datasets: [{
                    label: this.title,
                    data: data,
                    borderColor: '#0d6348',
                    backgroundColor: 'rgba(13, 99, 72, 0.1)',
                    borderWidth: 3,
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#0d6348',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointHoverBackgroundColor: '#e37926',
                    pointHoverBorderColor: '#ffffff',
                    pointHoverBorderWidth: 3
                }]
            }
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                scales: {
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            color: '#6c757d',
                            font: {
                                size: 12,
                                weight: '500'
                            }
                        }
                    },
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(108, 117, 125, 0.1)',
                            drawBorder: false
                        },
                        ticks: {
                            color: '#6c757d',
                            font: {
                                size: 12,
                                weight: '500'
                            },
                            callback: (value) => {
                                return new Intl.NumberFormat('ru-RU', {
                                    style: 'currency',
                                    currency: 'RUB',
                                    minimumFractionDigits: 0
                                }).format(value)
                            }
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'rect',
                            color: '#495057',
                            font: {
                                size: 14,
                                weight: '600'
                            },
                            padding: 20
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(13, 99, 72, 0.95)',
                        titleColor: '#ffffff',
                        bodyColor: '#ffffff',
                        borderColor: '#0d6348',
                        borderWidth: 2,
                        cornerRadius: 8,
                        displayColors: false,
                        titleFont: {
                            size: 14,
                            weight: '600'
                        },
                        bodyFont: {
                            size: 13,
                            weight: '500'
                        },
                        padding: 12,
                        callbacks: {
                            title: (context) => {
                                return context[0].label
                            },
                            label: (context) => {
                                const dataIndex = context.dataIndex
                                const item = this.rawChartData[dataIndex]
                                return [
                                    `💰 Маржинальность: ${this.formatCurrency(item.total_margin)}`,
                                    `📦 Заказов: ${item.successful_orders_count}`,
                                    `📊 Средняя маржа: ${this.formatCurrency(item.average_margin_per_order)}`
                                ]
                            }
                        }
                    }
                }
            }
        }
    },
    methods: {
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0
            }).format(amount)
        }
    },
    watch: {
        rawChartData: {
            handler(newData) {
                // Принудительно обновляем компонент при изменении данных
                if (newData && newData.length > 0) {
                    this.$nextTick(() => {
                        this.$forceUpdate()
                    })
                }
            },
            deep: true,
            immediate: true
        },
        loading: {
            handler(newLoading) {
                if (!newLoading && this.rawChartData && this.rawChartData.length > 0) {
                    this.$nextTick(() => {
                        this.$forceUpdate()
                    })
                }
            }
        }
    }
}
</script>

<style scoped>
.chart-wrapper {
    position: relative;
    height: 400px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(13, 99, 72, 0.1);
    border: 1px solid rgba(13, 99, 72, 0.1);
}
</style>
